"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useId } from "react";

// Wave 1 — wider, less height, moves RIGHT (from combined 37x22 viewBox)
const WAVE1_PATH =
  "M28.0625 6.11621C29.935 6.11621 30.9701 7.55565 31.5615 8.85449C32.1668 10.1837 32.5675 11.9169 32.9424 13.4541C33.3383 15.0771 33.7113 16.5126 34.2383 17.5469C34.7589 18.5686 35.2315 18.834 35.7178 18.834H37V21.3994H35.7178C33.7984 21.3994 32.6479 20.0751 31.9531 18.7119C31.2649 17.3617 30.8296 15.6177 30.4502 14.0615C30.0497 12.4192 29.707 10.9724 29.2266 9.91699C28.7319 8.83136 28.3373 8.68066 28.0625 8.68066C27.8048 8.68068 27.432 8.80843 26.9658 9.89258C26.5107 10.9512 26.2026 12.393 25.8184 14.0479C25.4555 15.6111 25.0216 17.3696 24.2852 18.7373C23.5228 20.153 22.2869 21.3984 20.2988 21.3984L20.2432 21.3828C20.18 21.3924 20.1156 21.3984 20.0498 21.3984C18.5344 21.3984 17.4074 20.8943 16.5576 20.082C15.7696 19.329 15.2808 18.3601 14.9062 17.6123C14.491 16.783 14.2068 16.2093 13.8223 15.7979C13.5134 15.4677 13.1374 15.2432 12.4424 15.2432C11.7474 15.2432 11.4151 15.463 11.1426 15.7715C10.7828 16.179 10.535 16.7374 10.1289 17.5898C9.76405 18.3555 9.27301 19.3402 8.42676 20.1055C7.52652 20.9195 6.33171 21.3984 4.72754 21.3984H1.28223C0.573934 21.3984 0 20.8242 0 20.1162C0.000205203 19.4084 0.574384 18.834 1.28223 18.834H4.72754C5.77107 18.8339 6.33121 18.543 6.70703 18.2031C7.13669 17.8144 7.44396 17.2599 7.8125 16.4873C8.13946 15.8013 8.56032 14.8201 9.21973 14.0732C9.96659 13.2276 10.9992 12.6777 12.4434 12.6777C13.8818 12.6779 14.9262 13.2231 15.6963 14.0469C16.3905 14.7897 16.8451 15.7543 17.2002 16.4639C17.5965 17.2551 17.9102 17.8264 18.3301 18.2275C18.6881 18.5697 19.1745 18.834 20.0498 18.834H20.2988C20.9754 18.834 21.5055 18.4902 22.0273 17.5215C22.5751 16.5047 22.9441 15.0838 23.3193 13.4678C23.6732 11.9433 24.0393 10.2049 24.6094 8.87891C25.1688 7.57856 26.1731 6.1164 28.0625 6.11621Z";

// Wave 2 — narrower, taller, moves LEFT (from combined 37x22 viewBox)
const WAVE2_PATH =
  "M20.8663 0C22.0196 0 22.9395 0.513835 23.6313 1.24613C24.2944 1.94784 24.7807 2.87747 25.1646 3.85376C25.93 5.80003 26.4432 8.35943 26.9468 10.7518C27.4665 13.22 27.9787 15.526 28.7226 17.2166C29.4962 18.9747 30.2597 19.5024 31 19.5024V22C28.5553 22 27.1704 20.09 26.3352 18.1913C25.4699 16.2249 24.9126 13.6557 24.406 11.2482C23.883 8.76514 23.4124 6.44878 22.7402 4.73831C22.4051 3.88623 22.0592 3.29232 21.7142 2.9276C21.3984 2.59313 21.1346 2.49761 20.8666 2.49761C20.5986 2.49761 20.3515 2.58966 20.0524 2.91499C19.7218 3.27436 19.3916 3.86385 19.0752 4.71656C18.439 6.43018 18.0142 8.74307 17.5144 11.2366C17.0311 13.6491 16.4767 16.2318 15.5551 18.2143C14.6417 20.1792 13.1435 22 10.5892 22H6V19.5024H10.5892C11.5622 19.5024 12.4019 18.8855 13.1887 17.1936C13.9672 15.5194 14.4751 13.2264 14.9687 10.7634C15.4458 8.38118 15.9125 5.81863 16.6338 3.87551C16.9961 2.89986 17.4614 1.96581 18.1117 1.25874C18.7937 0.517302 19.7097 0 20.8666 0H20.8663Z";

const DURATION = 2;
const HALF = DURATION / 2;

// Smooth fade curve
const FADE_EASE: [number, number, number, number] = [0.4, 0, 0.2, 1];

// Label fades inverse to waves
const LABEL_TIMES = [0, 0.06, 0.2, 0.36, 0.52, 0.66, 0.82, 0.94, 1];
const LABEL_OPACITY = [1, 0.9, 0.3, 0, 0, 0.15, 0.7, 0.95, 1];

// Wave transition — simple tween for X, fade curve for opacity
const waveTransition = (delay = 0) => ({
  x: {
    duration: HALF,
    ease: "easeInOut" as const,
    repeat: Number.POSITIVE_INFINITY,
    repeatDelay: HALF,
    delay,
  },
  opacity: {
    duration: HALF,
    ease: FADE_EASE,
    times: [0, 0.5, 1] as number[],
    repeat: Number.POSITIVE_INFINITY,
    repeatDelay: HALF,
    delay,
  },
});

const WaveSvg = ({
  path,
  gradientId,
  gradientStops,
  pathOpacity,
}: {
  path: string;
  gradientId: string;
  gradientStops: React.ReactNode;
  pathOpacity?: number;
}) => (
  <svg
    viewBox="0 0 37 22"
    preserveAspectRatio="xMidYMid meet"
    className="h-full w-full"
    aria-hidden="true"
  >
    <defs>
      <linearGradient
        id={gradientId}
        x1="0"
        y1="11"
        x2="37"
        y2="11"
        gradientUnits="userSpaceOnUse"
      >
        {gradientStops}
      </linearGradient>
    </defs>
    <path d={path} fill={`url(#${gradientId})`} opacity={pathOpacity ?? 1} />
  </svg>
);

export function PriceLoadingAnimation({ label }: { label: string }) {
  const shouldReduceMotion = useReducedMotion();
  const dur = shouldReduceMotion ? 0 : DURATION;
  const uid = useId().replace(/:/g, "");

  return (
    <div
      className="flex w-full items-center rounded-full"
      style={{
        height: 40,
        paddingLeft: 4,
        paddingRight: 16,
        gap: 10,
        backgroundColor: "rgba(214, 238, 255, 0.6)",
        overflow: "visible",
      }}
    >
      {/* Circle area */}
      <div
        className="relative flex shrink-0 items-center justify-center"
        style={{ width: 32, height: 32 }}
      >
        {/* Ring glow — spring-driven scale pulse */}
        <motion.div
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 60,
            height: 60,
            top: "50%",
            left: "50%",
            marginTop: -30,
            marginLeft: -30,
            background: [
              "radial-gradient(circle at center,",
              "transparent 48%,",
              "rgba(255,255,255,0.3) 51%,",
              "rgba(255,255,255,1) 55%,",
              "rgba(255,255,255,0.7) 62%,",
              "rgba(210,235,255,0.45) 72%,",
              "rgba(185,220,250,0.15) 84%,",
              "transparent 100%)",
            ].join(" "),
            willChange: "transform, opacity",
          }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{
            opacity: [0, 1, 0],
            scale: [0.9, 1.08, 0.98],
            boxShadow: [
              "0 0 8px 2px rgba(160,210,255,0)",
              "0 0 18px 8px rgba(160,210,255,0.4)",
              "0 0 8px 2px rgba(160,210,255,0)",
            ],
          }}
          transition={{
            duration: HALF,
            ease: FADE_EASE,
            times: [0, 0.5, 1],
            repeat: Number.POSITIVE_INFINITY,
            repeatDelay: HALF,
            delay: 0.3,
          }}
        />

        {/* Blue circle */}
        <div
          className="relative overflow-hidden rounded-full"
          style={{
            width: 32,
            height: 32,
            backgroundColor: "#0099FF",
            zIndex: 1,
          }}
        >
          {/* Wave 1 — drifts RIGHT + slight upward arc */}
          <motion.div
            className="absolute flex items-center justify-center"
            style={{
              width: "110%",
              height: "100%",
              left: "-5%",
              top: 0,
              willChange: "transform, opacity",
            }}
            animate={{ x: 8, opacity: [0, 1, 0] }}
            transition={waveTransition()}
          >
            <WaveSvg
              path={WAVE1_PATH}
              gradientId={`w1g-${uid}`}
              gradientStops={
                <>
                  <stop stopColor="white" stopOpacity="0" />
                  <stop offset="0.55" stopColor="white" stopOpacity="1" />
                  <stop offset="1" stopColor="white" stopOpacity="0" />
                </>
              }
            />
          </motion.div>

          {/* Wave 2 — drifts LEFT + slight downward arc */}
          <motion.div
            className="absolute flex items-center justify-center"
            style={{
              width: "110%",
              height: "100%",
              left: "-5%",
              top: 0,
              willChange: "transform, opacity",
            }}
            animate={{ x: -8, opacity: [0, 1, 0] }}
            transition={waveTransition(0.04)}
          >
            <WaveSvg
              path={WAVE2_PATH}
              gradientId={`w2g-${uid}`}
              pathOpacity={0.7}
              gradientStops={
                <>
                  <stop stopColor="white" stopOpacity="0" />
                  <stop offset="0.19" stopColor="white" stopOpacity="1" />
                  <stop offset="0.8" stopColor="white" stopOpacity="1" />
                  <stop offset="1" stopColor="white" stopOpacity="0" />
                </>
              }
            />
          </motion.div>
        </div>
      </div>

      {/* Label — words slide in from ring, slide out to the right */}
      <span
        className="font-medium flex items-center gap-[0.3em]"
        style={{
          color: "#00AAFF",
          fontSize: 16,
          letterSpacing: "-0.32px",
          marginLeft: 16,
        }}
        aria-label={label}
      >
        {label.split(" ").map((word, i) => (
          <motion.span
            // biome-ignore lint/suspicious/noArrayIndexKey: static label, stable order
            key={`${word}-${i}`}
            style={{
              willChange: "transform, opacity, filter",
              display: "inline-block",
            }}
            animate={{
              opacity: [0, 0, 1, 1, 0, 0],
              x: [-6, -6, 0, 0, 10, 10],
              filter: [
                "blur(6px)",
                "blur(6px)",
                "blur(0px)",
                "blur(0px)",
                "blur(4px)",
                "blur(4px)",
              ],
            }}
            transition={{
              duration: DURATION,
              ease: FADE_EASE,
              times: [0, 0.28, 0.34, 0.86, 0.92, 1],
              repeat: Number.POSITIVE_INFINITY,
              delay: i * 0.08,
            }}
          >
            {word}
          </motion.span>
        ))}
      </span>
    </div>
  );
}
